import React, { useState, useEffect, forwardRef } from 'react';

// Module-level registry for failed URLs to prevent repeated requests globally
const failedUrls = new Set();

const SafeImage = forwardRef(({
  src,
  alt,
  className,
  style,
  fallback, // React element or URL string
  crossOrigin,
  loading = 'lazy',
  onError,
  onLoad,
  placeholder,
  ...props
}, ref) => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!src) {
      setError(true);
      setIsLoading(false);
      return;
    }

    if (failedUrls.has(src)) {
      setError(true);
      setIsLoading(false);
    } else {
      setError(false);
      setIsLoading(true);
    }
  }, [src]);

  const handleLoad = (e) => {
    setIsLoading(false);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    if (src) {
      failedUrls.add(src);
    }
    setError(true);
    setIsLoading(false);
    if (onError) onError(e);
  };

  if (error) {
    if (React.isValidElement(fallback)) {
      return fallback;
    }
    if (typeof fallback === 'string' && fallback) {
      return (
        <img
          ref={ref}
          src={fallback}
          alt={alt}
          className={className}
          style={style}
          loading={loading}
          {...props}
        />
      );
    }
    return null;
  }

  // If there's no custom placeholder, render the img directly to avoid wrapping divs and breaking CSS layout/selectors
  if (!placeholder) {
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        crossOrigin={crossOrigin}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    );
  }

  return (
    <div style={{ position: 'relative', width: style?.width || '100%', height: style?.height || '100%', ...style }}>
      {isLoading && placeholder}
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={className}
        style={{
          ...style,
          display: isLoading ? 'none' : (style?.display || 'block'),
        }}
        loading={loading}
        crossOrigin={crossOrigin}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </div>
  );
});

SafeImage.displayName = 'SafeImage';

export default SafeImage;
